import { CheckIcon } from 'lucide-react';
import { parseId } from '@runloop/reflex-contract';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import {
  EntityIdBadgeChip,
  ENTITY_ID_BADGE_CHIP_CLASS,
} from '@reflex/ui/components/content/EntityIdBadge';
import type { EntityIdBadgeHostProps } from '@reflex/ui/components/registry/PluginComponentRegistry';
import { useEntityIdPreview } from '@/components/resource-preview/useEntityIdPreview';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { ResourcePreview } from '@/components/resource-preview/ResourcePreview';

/**
 * Host implementation of the entity-id badge, registered into the
 * `@reflex/ui` component registry so the chat plugin's content scanner (which
 * imports the `@reflex/ui` `EntityIdBadge`) picks it up. It resolves the id's
 * 3-letter prefix to a preview registration via {@link useEntityIdPreview} and,
 * when one exists, renders the id chip with the registry icon inside a hover
 * card showing the shared {@link ResourcePreview} - the same popover an explicit
 * `<ResourceChip>` gets. When no preview owns the prefix it falls back to the
 * plain, copyable `EntityIdBadgeChip`.
 */
export function EntityIdBadgeHost({ match, groups }: EntityIdBadgeHostProps) {
  const id = match;
  const prefix = groups[0] || parseId(id)?.prefix || '';
  const preview = useEntityIdPreview(prefix);
  const { copied, copy } = useCopyToClipboard();

  // No preview owns this prefix - render the plain copyable chip.
  if (!preview) {
    return <EntityIdBadgeChip match={match} groups={groups} />;
  }

  const Icon = preview.icon;
  const kindLabel = preview.label.toLowerCase();
  const copyLabel = `Copy ${kindLabel} id ${id}`;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => copy(id)}
          aria-label={copyLabel}
          title={copied ? 'Copied!' : `Copy ${kindLabel} id`}
          data-testid="entity-id-badge"
          data-copied={copied || undefined}
          className={ENTITY_ID_BADGE_CHIP_CLASS}
        >
          {copied ? (
            <CheckIcon className="size-3 shrink-0 text-status-success" aria-hidden="true" />
          ) : (
            <Icon className="size-3 shrink-0 opacity-70 group-hover:opacity-100" />
          )}
          <span className="truncate select-all">{id}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={`${preview.label} ${id}`}
      >
        <ResourcePreview kind={preview.kind} id={id} fallbackName={id} />
      </HoverCardContent>
    </HoverCard>
  );
}
